import { type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { userQueryOptions } from "~/user/data-source/user-query-options";
import { findCurrentRegistryUser } from "~/user/model/current-user-identity";
import { resolveUserDisplayNameOrUnknown } from "~/user/utils/format-user-name";

import { useCurrentUserIdentity } from "./use-current-user-identity";

// Which fetched list (by dataUpdatedAt) and identity have already had their one retry-on-miss,
// per QueryClient — shared so several mounted consumers agree on a single check.
const retriedMissByClient = new WeakMap<QueryClient, { dataUpdatedAt: number; identityKey: string }>();

export function useCurrentUser(options?: { enabled?: boolean }) {
  const identity = useCurrentUserIdentity();
  const identityKey = `${identity.principalId ?? ""}:${identity.upstreamUserId ?? ""}`;
  const signedIn = !!identity.principalId || !!identity.upstreamUserId;
  const enabled = signedIn && (options?.enabled ?? true);
  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    ...userQueryOptions.all(),
    enabled,
    select: (users) => findCurrentRegistryUser(users, identity),
  });

  // A list that loaded without the signed-in person might just be stale — the registry may have
  // finished writing their record moments ago. Ask once more before treating it as final.
  const isMiss = usersQuery.isSuccess && !usersQuery.data;
  const retryMarker = retriedMissByClient.get(queryClient);
  const hasRetriedForCurrentList =
    retryMarker?.dataUpdatedAt === usersQuery.dataUpdatedAt && retryMarker.identityKey === identityKey;

  useEffect(() => {
    if (!enabled || !isMiss || usersQuery.isFetching || hasRetriedForCurrentList) return;
    // cancelRefetch: false joins an in-flight fetch instead of duplicating it. The marker uses
    // the retry's own result, not the pre-retry dataUpdatedAt, which the retry itself changes.
    void usersQuery.refetch({ cancelRefetch: false }).then((result) => {
      retriedMissByClient.set(queryClient, { dataUpdatedAt: result.dataUpdatedAt, identityKey });
    });
  }, [enabled, isMiss, usersQuery, hasRetriedForCurrentList, queryClient, identityKey]);

  // `isPending`, not `isLoading`: a disabled query is pending with `fetchStatus: "idle"`, so
  // `isLoading` is false there and the unresolved query would fall through to `"unknown"` — which
  // means "settled, no such user" and is a different answer from "not asked yet".
  if (!signedIn || usersQuery.isPending) return { status: "loading" } as const;
  if (usersQuery.isError) return { status: "error" } as const;
  if (!usersQuery.data) {
    // Disabled: no retry will ever be issued, so a cached miss is final, not still loading.
    if (!enabled) return { status: "unknown" } as const;
    if (usersQuery.isFetching || !hasRetriedForCurrentList) return { status: "loading" } as const;
    return { status: "unknown" } as const;
  }
  return {
    status: "ready",
    userId: usersQuery.data.id,
    name: resolveUserDisplayNameOrUnknown(usersQuery.data),
  } as const;
}
